import {useQuery, useQueryClient} from '@tanstack/react-query'
import {createContext, type ReactNode, useCallback, useContext, useMemo} from 'react'

import {logout as logoutRequest, UnauthorizedError} from './auth-api'
import {meQueryKey, meQueryOptions} from './me-query'
import type {AuthUser} from './types'

export type AuthContextValue = {
    user: AuthUser | null
    isLoading: boolean
    signIn: (user: AuthUser) => void
    refreshUser: () => Promise<AuthUser | null>
    logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({children}: {children: ReactNode}) {
    const queryClient = useQueryClient()
    const {data: user = null, isLoading, refetch} = useQuery(meQueryOptions)

    const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
        const result = await refetch()
        if (result.error instanceof UnauthorizedError) {
            queryClient.setQueryData(meQueryKey, null)
            return null
        }
        if (result.error) {
            throw result.error
        }
        return result.data ?? null
    }, [queryClient, refetch])

    const signIn = useCallback(
        (nextUser: AuthUser) => {
            queryClient.setQueryData(meQueryKey, nextUser)
        },
        [queryClient],
    )

    const logout = useCallback(async () => {
        await logoutRequest()
        queryClient.removeQueries({queryKey: meQueryKey})
        window.location.href = '/login'
    }, [queryClient])

    const value = useMemo(
        () => ({
            user,
            isLoading,
            signIn,
            refreshUser,
            logout,
        }),
        [user, isLoading, signIn, refreshUser, logout],
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext(): AuthContextValue {
    const context = useContext(AuthContext)
    if (context == null) {
        throw new Error('useAuthContext must be used within AuthProvider')
    }
    return context
}
